import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Check, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const plans = [
  {
    id: 'starter',
    name: 'Starter',
    price: 29,
    priceINR: 2900,
    period: 'month',
    description: 'Perfect for small businesses',
    features: [
      '1,000 messages/month',
      '1 WhatsApp number',
      'Basic analytics',
      'Email support',
      'Contact management',
    ],
    popular: false,
  },
  {
    id: 'professional',
    name: 'Professional',
    price: 99,
    priceINR: 9900,
    period: 'month',
    description: 'For growing teams',
    features: [
      '10,000 messages/month',
      '3 WhatsApp numbers',
      'Advanced analytics',
      'Priority support',
      'Automation & chatbots',
      'Team collaboration',
      'API access',
    ],
    popular: true,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 299,
    priceINR: 29900,
    period: 'month',
    description: 'For large organizations',
    features: [
      'Unlimited messages',
      'Unlimited numbers',
      'Custom analytics',
      'Dedicated account manager',
      'Advanced automation',
      'White-label solution',
      'Custom integrations',
      'SLA guarantee',
    ],
    popular: false,
  },
];

export function PricingPage() {
  const { user, token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('razorpay'); // 'razorpay' or 'unlimit'

  const handleSelectPlan = async (plan) => {
    if (!isAuthenticated) {
      navigate('/signup');
      return;
    }

    setLoadingPlan(plan.id);

    try {
      if (paymentMethod === 'razorpay') {
        await handleRazorpayPayment(plan);
      } else {
        await handleUnlimitPayment(plan);
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('Payment failed. Please try again.');
    } finally {
      setLoadingPlan(null);
    }
  };

  const handleRazorpayPayment = async (plan) => {
    // Create order
    const response = await fetch(`${API_URL}/api/payments/create-order`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ plan_id: plan.id, currency: 'INR' })
    });

    if (!response.ok) {
      throw new Error('Failed to create order');
    }

    const orderData = await response.json();

    // Load Razorpay
    const options = {
      key: orderData.key_id,
      amount: orderData.amount,
      currency: orderData.currency,
      name: 'GiriChat',
      description: `${orderData.plan_name} Plan Subscription`,
      order_id: orderData.order_id,
      handler: async function (response) {
        // Verify payment
        const verifyResponse = await fetch(`${API_URL}/api/payments/verify`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature
          })
        });

        if (verifyResponse.ok) {
          alert('Payment successful! Your subscription is now active.');
          navigate('/dashboard');
        } else {
          alert('Payment verification failed. Please contact support.');
        }
      },
      prefill: {
        name: user?.full_name || '',
        email: user?.email || '',
        contact: user?.phone || ''
      },
      theme: {
        color: '#00D95F'
      }
    };

    // Check if Razorpay is loaded
    if (window.Razorpay) {
      const rzp = new window.Razorpay(options);
      rzp.open();
    } else {
      // Load Razorpay script dynamically
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => {
        const rzp = new window.Razorpay(options);
        rzp.open();
      };
      document.body.appendChild(script);
    }
  };

  const handleUnlimitPayment = async (plan) => {
    // Create Unlimit payment
    const response = await fetch(`${API_URL}/api/payments/unlimit/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ plan_id: plan.id, currency: 'USD' })
    });

    if (!response.ok) {
      throw new Error('Failed to create payment');
    }

    const paymentData = await response.json();
    
    // In production, redirect to Unlimit payment page
    alert(`Unlimit payment initiated. Payment ID: ${paymentData.payment_id}\nAmount: $${paymentData.amount} USD`);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="pricing-page">
      <Header />
      
      <div className="flex-1 bg-gradient-to-b from-[#E8F9EE] to-white py-12 sm:py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Choose Your Plan
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Select the perfect plan for your business needs
            </p>

            {/* Payment Method Toggle */}
            <div className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full p-1 shadow-sm">
              <button
                onClick={() => setPaymentMethod('razorpay')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  paymentMethod === 'razorpay'
                    ? 'bg-[#00D95F] text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Pay with Razorpay (INR)
              </button>
              <button
                onClick={() => setPaymentMethod('unlimit')}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  paymentMethod === 'unlimit'
                    ? 'bg-[#00D95F] text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                International Cards (USD)
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto">
            {plans.map((plan) => (
              <Card
                key={plan.id}
                className={`p-6 sm:p-8 relative bg-white ${
                  plan.popular
                    ? 'border-2 border-[#00D95F] shadow-xl md:scale-105'
                    : 'border border-gray-200'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-[#00D95F] text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </div>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <p className="text-sm text-gray-600 mb-4">{plan.description}</p>
                  <div className="flex items-baseline justify-center gap-1">
                    {paymentMethod === 'razorpay' ? (
                      <>
                        <span className="text-4xl sm:text-5xl font-bold text-gray-900">₹{plan.priceINR / 100}</span>
                        <span className="text-gray-600">/{plan.period}</span>
                      </>
                    ) : (
                      <>
                        <span className="text-4xl sm:text-5xl font-bold text-gray-900">${plan.price}</span>
                        <span className="text-gray-600">/{plan.period}</span>
                      </>
                    )}
                  </div>
                </div>

                <Button
                  onClick={() => handleSelectPlan(plan)}
                  disabled={loadingPlan === plan.id}
                  className={`w-full mb-6 ${
                    plan.popular
                      ? 'bg-[#00D95F] hover:bg-[#00C054] text-white'
                      : 'bg-white border border-gray-300 text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {loadingPlan === plan.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : isAuthenticated ? (
                    'Subscribe Now'
                  ) : (
                    'Get Started'
                  )}
                </Button>

                <div className="space-y-3">
                  {plan.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-[#E8F9EE] flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-[#00D95F]" />
                      </div>
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>

          {/* Payment Badges */}
          <div className="mt-12 text-center">
            <p className="text-sm text-gray-500 mb-4">Secure payments powered by</p>
            <div className="flex flex-wrap justify-center gap-6 items-center">
              <div className="flex items-center gap-2 text-gray-600">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect width="24" height="24" rx="4" fill="#072654"/>
                  <text x="12" y="16" fill="white" fontSize="8" textAnchor="middle">Rp</text>
                </svg>
                <span className="text-sm font-medium">Razorpay</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect width="24" height="24" rx="4" fill="#1A1A2E"/>
                  <text x="12" y="16" fill="white" fontSize="8" textAnchor="middle">U</text>
                </svg>
                <span className="text-sm font-medium">Unlimit</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect width="24" height="24" rx="4" fill="#00D95F"/>
                  <path d="M7 12h10M12 7v10" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <span className="text-sm font-medium">256-bit SSL</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
